import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiLinkedin, FiMapPin, FiChevronDown, FiUser } from 'react-icons/fi';
import { ResumeData } from '../types/resume.types';

interface ContactDropdownProps {
  data: ResumeData;
}

const ContactDropdown: React.FC<ContactDropdownProps> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const contactItems = [
    {
      icon: FiMail,
      label: 'Email',
      value: data.personalInfo.email,
      href: `mailto:${data.personalInfo.email}`,
      display: data.personalInfo.email
    },
    {
      icon: FiLinkedin,
      label: 'LinkedIn',
      value: data.personalInfo.linkedin || '#',
      href: data.personalInfo.linkedin || '#',
      display: 'LinkedIn Profile',
      external: true
    },
    {
      icon: FiMapPin,
      label: 'Location',
      value: data.personalInfo.location,
      display: data.personalInfo.location
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-all duration-200 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
        aria-label="Contact Information"
      >
        <FiUser size={16} />
        <span className="hidden sm:inline">Contact</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FiChevronDown size={14} />
        </motion.div>
      </motion.button>

      {/* Dropdown Content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-600 backdrop-blur-md z-50"
          >
            <div className="p-4 space-y-3">
              <div className="flex items-center space-x-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center text-white font-bold">
                  {data.personalInfo.name.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                    {data.personalInfo.name}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {data.personalInfo.title}
                  </p>
                </div>
              </div>

              {contactItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.external ? '_blank' : undefined}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 group"
                      >
                        <Icon 
                          size={16} 
                          className="text-gray-500 dark:text-gray-400 group-hover:text-primary-500 transition-colors" 
                        />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            {item.label}
                          </p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {item.display}
                          </p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center space-x-3 p-2 rounded-lg">
                        <Icon 
                          size={16} 
                          className="text-gray-500 dark:text-gray-400" 
                        />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            {item.label}
                          </p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {item.display}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactDropdown;